/**
 * Batch #8 — Page Admin de mapping visuel des signatures PDF
 *
 * Onglet « Modèles PDF » de Documents à signer (/admin/client-documents?tab=modeles)
 *
 * Permet à l'Admin de :
 *  1. Lister/uploader des templates PDF (Bail TAL, autres formulaires)
 *  2. Ouvrir un éditeur drag & drop pour placer des étiquettes (Signature, Date)
 *     sur les pages du PDF (rendues en image PNG)
 *  3. Sauvegarder les coordonnées dans la DB
 *  4. Prévisualiser le rendu avec des rectangles colorés simulant les placements
 *  5. Activer un template (qui devient celui utilisé par le PDF merger)
 *
 * Architecture :
 *  - Image PNG de chaque page rendue par pdftoppm côté backend (GET .../page/{n})
 *  - Image affichée dans un div parent positionné `relative`
 *  - Étiquettes = divs `absolute` avec custom drag handler (onMouseDown/Move/Up)
 *  - Sidebar : palette d'étiquettes prédéfinies (cliquer pour ajouter au centre)
 *  - Coordonnées en POINTS PDF (origine top-left), conversion automatique via
 *    le ratio image_displayed_width / pdf_width.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { FileText, Trash2, Upload, Loader2, CheckCircle2, FileSignature } from "lucide-react";

// ===========================================================================
// Liste + bouton upload
// ===========================================================================
export default function PdfTemplatesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const navigate = useNavigate();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/pdf-templates");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const onArchive = async (id) => {
    if (!window.confirm("Archiver ce template ?")) return;
    try {
      await api.delete(`/admin/pdf-templates/${id}`);
      toast.success("Template archivé");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.put(`/admin/pdf-templates/${item.id}`, { is_active: !item.is_active });
      toast.success(item.is_active ? "Template désactivé" : "Template activé · utilisé pour la prochaine génération");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div data-testid="pdf-templates-page">
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-cyan-600" />
              Modèles PDF
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Importez vos formulaires PDF (Bail TAL, documents à signer…) et placez visuellement les zones de signature.
              Les modèles de type « Document à signer » apparaissent dans « Nouveau document ».
            </p>
          </div>
          <button
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold"
            data-testid="pdf-templates-upload-btn"
          >
            <Upload className="w-4 h-4" /> Importer un PDF
          </button>
        </div>

        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-300 mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-xl border text-slate-400 italic">
            Aucun template configuré. Cliquez sur « Importer un PDF » pour commencer.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((t) => (
              <div key={t.id} className="bg-white rounded-xl border p-4 hover:shadow-md transition" data-testid={`pdf-template-card-${t.key}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-slate-800 truncate">{t.name}</h3>
                    <p className="text-[11px] text-slate-500 mt-0.5 font-mono">
                      clé : <code>{t.key}</code> · {t.page_count} pages · {(t.fields || []).length} zones définies
                    </p>
                    {/* Batch #14.17 — Type badge */}
                    <span className={`mt-1.5 inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      (t.template_type || "bail") === "document"
                        ? "bg-rose-50 text-rose-700 border border-rose-200"
                        : "bg-blue-50 text-blue-700 border border-blue-200"
                    }`} data-testid={`pdf-template-type-${t.key}`}>
                      {(t.template_type || "bail") === "document" ? "DOCUMENT À SIGNER" : "BAIL"}
                    </span>
                  </div>
                  {t.is_active ? (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> ACTIF
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold">
                      Inactif
                    </span>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-1 flex-wrap">
                  <button
                    onClick={() => navigate(`/admin/pdf-templates/${t.id}`)}
                    className="px-2 py-1 rounded-md bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold inline-flex items-center gap-1"
                    data-testid={`pdf-template-edit-${t.key}`}
                  >
                    <FileText className="w-3 h-3" /> Éditer les zones
                  </button>
                  <button
                    onClick={() => toggleActive(t)}
                    className={`px-2 py-1 rounded-md text-xs font-semibold ${
                      t.is_active
                        ? "bg-rose-50 text-rose-600 hover:bg-rose-100"
                        : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    }`}
                    data-testid={`pdf-template-toggle-${t.key}`}
                  >
                    {t.is_active ? "Désactiver" : "Activer"}
                  </button>
                  <button
                    onClick={() => onArchive(t.id)}
                    className="px-2 py-1 rounded-md bg-white border text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 text-xs inline-flex items-center gap-1"
                    data-testid={`pdf-template-archive-${t.key}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showUpload && (
        <UploadTemplateModal
          onClose={() => setShowUpload(false)}
          onUploaded={(t) => { setShowUpload(false); fetchAll(); navigate(`/admin/pdf-templates/${t.id}`); }}
          uploading={uploading}
          setUploading={setUploading}
        />
      )}
    </div>
  );
}


function UploadTemplateModal({ onClose, onUploaded, uploading, setUploading }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [templateType, setTemplateType] = useState("bail");
  const [progress, setProgress] = useState(0);
  const [errMsg, setErrMsg] = useState("");

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setErrMsg("");
    if (!f) { setFile(null); return; }
    // Validation côté client
    if (!f.name.toLowerCase().endsWith(".pdf") && f.type !== "application/pdf") {
      setErrMsg("Le fichier doit être un PDF (.pdf)");
      setFile(null);
      e.target.value = "";  // reset input
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setErrMsg(`Fichier trop volumineux (${(f.size/1024/1024).toFixed(1)} Mo · max 10 Mo)`);
      setFile(null);
      e.target.value = "";
      return;
    }
    setFile(f);
    // Auto-fill name if empty
    if (!name) setName(f.name.replace(/\.pdf$/i, ""));
  };

  const submit = async () => {
    if (!file) return toast.error("Choisissez un PDF");
    setUploading(true);
    setProgress(0);
    setErrMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (name) fd.append("name", name);
      if (key) fd.append("key", key);
      fd.append("template_type", templateType);
      // Batch #13.2 — Ne PAS définir Content-Type manuellement : axios + FormData
      // ajoute automatiquement le boundary correct. Sinon le multipart casse.
      const r = await api.post(
        "/admin/pdf-templates",
        fd,
        {
          timeout: 90_000,  // 90s pour gros PDFs
          onUploadProgress: (evt) => {
            if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        },
      );
      toast.success("Template importé");
      onUploaded(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail
        || (e.code === "ECONNABORTED" ? "Délai dépassé · réessayez avec un PDF plus petit"
        : e.message || "Erreur upload");
      setErrMsg(typeof detail === "string" ? detail : "Erreur upload");
      toast.error(typeof detail === "string" ? detail : "Erreur upload");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="pdf-template-upload-modal">
        <h3 className="font-bold text-slate-800 inline-flex items-center gap-2">
          <Upload className="w-4 h-4 text-cyan-600" /> Importer un nouveau template
        </h3>
        <label className="block">
          <span className="text-[10px] text-slate-600 font-semibold uppercase tracking-wider">Fichier PDF</span>
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={onFileChange}
            className="cc-input mt-1 text-xs"
            data-testid="pdf-template-upload-file"
            disabled={uploading}
          />
          {file && (
            <div className="text-[11px] text-slate-600 mt-1">
              📄 {file.name} · <strong>{(file.size/1024).toFixed(0)} Ko</strong>
            </div>
          )}
        </label>
        <label className="block">
          <span className="text-[10px] text-slate-600 font-semibold uppercase tracking-wider">Nom (optionnel)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className="cc-input mt-1" placeholder="Bail TAL — Version 2026" disabled={uploading} />
        </label>
        <label className="block">
          <span className="text-[10px] text-slate-600 font-semibold uppercase tracking-wider">Type de modèle</span>
          <select
            value={templateType}
            onChange={(e) => setTemplateType(e.target.value)}
            className="cc-input mt-1"
            disabled={uploading}
            data-testid="pdf-template-upload-type"
          >
            <option value="bail">Bail (TAL)</option>
            <option value="document">Document à signer (mandat, formulaire…)</option>
          </select>
          <span className="text-[10px] text-slate-400 italic">
            {templateType === "bail" ? "Utilisé pour les baux locataire/propriétaire" : "Envoyé à un client unique pour signature simple"}
          </span>
        </label>
        <label className="block">
          <span className="text-[10px] text-slate-600 font-semibold uppercase tracking-wider">Clé unique (optionnel)</span>
          <input value={key} onChange={(e) => setKey(e.target.value)} className="cc-input mt-1 font-mono" placeholder="bail_tal_v2" disabled={uploading} />
        </label>
        {uploading && progress > 0 && (
          <div className="space-y-1">
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-cyan-600 transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="text-[10px] text-slate-500 text-right">{progress}%</div>
          </div>
        )}
        {errMsg && (
          <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-2" data-testid="pdf-template-upload-error">
            {errMsg}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg border text-sm" disabled={uploading}>Annuler</button>
          <button onClick={submit} disabled={!file || uploading} className="px-3 py-1.5 rounded-lg bg-cyan-600 text-white text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="pdf-template-upload-submit">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Importer
          </button>
        </div>
      </div>
    </div>
  );
}



// PdfTemplateEditor extrait dans ./pdfTemplates/PdfTemplateEditor.jsx (Batch #14.221)
